import pipe from 'lodash/fp/pipe';
import formatWithOptions from 'date-fns/fp/formatWithOptions';
import getISOWeek from 'date-fns/fp/getISOWeek';
import parseISO from 'date-fns/fp/parseISO';
import startOfDayFp from 'date-fns/fp/startOfDay';
import getISODay from 'date-fns/fp/getISODay';
import startOfISOWeek from 'date-fns/fp/startOfISOWeek';
import formatFp from 'date-fns/fp/format';
import subMilliseconds from 'date-fns/fp/subMilliseconds';
import addDays from 'date-fns/addDays';
import eachDayOfInterval from 'date-fns/eachDayOfInterval';
import format from 'date-fns/format';
import startOfToday from 'date-fns/startOfToday';
import startOfDay from 'date-fns/startOfDay';
import min from 'date-fns/min';
import endOfDay from 'date-fns/endOfDay';
import {AppLanguage, systemDateFormat, knownLocales} from './constants';
import isSameDay from 'date-fns/isSameDay';
import isBefore from 'date-fns/isBefore';
import isAfter from 'date-fns/isAfter';
import {addHours, isWeekend, set, startOfWeek} from 'date-fns';

export type DayName = 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday' | 'Sunday';

/**
 * Converts the ISO string into a date representing the beginning of the specified day
 */
export const getStartOfDay = pipe(parseISO, startOfDayFp);

/**
 * Converts the ISO string into a date representing the beginning of this week (in ISO)
 */
export const getStartOfWeek = pipe(getStartOfDay, startOfISOWeek);

/**
 * Gets the start of this week (in ISO)
 */
export const getStartOfThisWeek = pipe(startOfToday, startOfISOWeek);

/**
 * Gets the week number of the desired ISO date string
 */
export const getWeekNumber = pipe(getStartOfDay, getISOWeek);

/**
 * Converts a day name into an ISO day (number)
 * @param day The day name
 * @returns The ISO day
 */
export const dayNameToISODay = (day: DayName) => {
  switch (day) {
    case 'Monday':
      return 1;
    case 'Tuesday':
      return 2;
    case 'Wednesday':
      return 3;
    case 'Thursday':
      return 4;
    case 'Friday':
      return 5;
    case 'Saturday':
      return 6;
    case 'Sunday':
      return 7;
  }
};

/**
 * Converts an array of day names to their corresponding ISO days
 * @param days The days to be converted
 * @returns The array of ISO days (number)
 */
export const dayNamesToISODays = (days: DayName[]) => days.map(dayNameToISODay) as number[];

/**
 * Returns whether the specified date is contained in the array of ISO days
 * @param date The date to be checked for
 * @returns A boolean indicating whether the date is contained or not
 */
export const isoDaysContainsDate = (date: Date) => (isoDays: number[]) => isoDays.includes(getISODay(date));

export const formatDateOptions = (language: AppLanguage) => {
  const locale = knownLocales[language];
  return {
    locale: locale,
  };
};

/**
 * Formats a date string into the desired language and format
 * @param outputFormat The desired format
 * @param language The desired language
 * @returns The formatted string in the desired language
 */
export const formatDateString = (outputFormat: string, language: AppLanguage) => (dateString: string) => {
  if (!dateString || !outputFormat) {
    return '';
  }

  const options = formatDateOptions(language);
  const formatDate = pipe(parseISO, formatWithOptions(options)(outputFormat));
  return formatDate(dateString);
};

/**
 * Formats a date in the desired language and format
 * @param date The date
 * @param fmt The format
 * @param language The language
 * @returns The formatted string in the desired language
 */
export const formatDate = (date: Date | number, fmt: string, language: AppLanguage) => {
  return format(date, fmt, formatDateOptions(language));
};

/**
 * Formats the date string into the system date format (yyyy-MM-dd), mostly to perform lookups in dictionaries
 */
export const formatDateStringAsKey = pipe(parseISO, formatFp(systemDateFormat));

export const formatDateAsKey = formatFp(systemDateFormat);

export const endOfPreviousDay = pipe(startOfDayFp, subMilliseconds(1));

/**
 * Forces a iso UTC date string to have the kind unknown, so when parsed back with parseISO the date assumes the local timezone.
 * @param dateString The date string
 * @returns The date string with its kind unknown
 */
export const removeKind = (dateString: string) => (dateString.endsWith('Z') ? dateString.slice(0, -1) : dateString);

/**
 * Forces an ISO UTC date to be parsed as local by ignoring the kind.
 * This exists mainly because we get meetings flagged as all day from MS graph as UTC dates where they are not UTC.
 * Ideal solution here will probably be to somehow tell the backend which timezone we're interested into, so the backend converts the dates accordingly before reaching the app.
 */
export const parseLocalDateWorkaround = pipe(removeKind, parseISO);

/**
 * Calculates non-overlapping chunks of full days (from 00:00 to 23:59:59) based on the chunk size
 * @param from From
 * @param to To
 * @param chunkSize Desired chunk size
 * @returns An array of non-overlaping chunks of full days (an array of two positions) based on the specified dates
 */
export const chunkifyRange = (from: Date, to: Date, chunkSize: number) => {
  const range = eachDayOfInterval({start: from, end: to}).length;
  const chunks = Math.ceil(range / chunkSize);
  return Array.from(Array(chunks), (_, i) => [
    addDays(startOfDay(from), i * chunkSize),
    min([endOfDay(to), endOfPreviousDay(addDays(startOfDay(from), i * chunkSize + chunkSize))]),
  ]);
};

export const isBeforeToday = (date: Date, today: Date) => !isSameDay(date, today) && isBefore(date, today);

export const isAfterToday = (date: Date, today: Date) => !isSameDay(date, today) && isAfter(date, today);

export const toISODateString = (date: Date) => date.toISOString();

export const fromISODateString = (isoDateString: string) => parseISO(isoDateString);

export const getNextHour = () => {
  const currentDateTime = new Date();
  const dateWithNextHour = addHours(currentDateTime, 1);
  return set(dateWithNextHour, {minutes: 0, seconds: 0, milliseconds: 0});
};

export const setTime = (date: Date, hours: number, minutes: number) =>
  set(date, {hours, minutes, seconds: 0, milliseconds: 0});

export const getLocalizedWeekDayNames = (localeID: string, shortenToLength?: number) => {
  const startOfCurrentWeek = startOfWeek(new Date());
  return Array(7)
    .fill('')
    .map((_, index) => {
      const dayInAWeekDate = addDays(startOfCurrentWeek, index);

      const dayInAWeek = format(dayInAWeekDate, 'EEEE', {locale: knownLocales[localeID]});

      if (!shortenToLength) {
        return dayInAWeek;
      }

      return dayInAWeek.substring(0, shortenToLength);
    });
};

/** @returns Date with zeroed-out seconds and milliseconds */
export const stripSecondsAndMilliseconds = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes());

export const roundDownDurationIfMoreThanDay = (duration: Duration): Duration => {
  const {years, months, weeks, days} = duration;

  if (years) return {years};
  if (months) return {months};
  if (weeks) return {weeks};
  if (days) return {days};

  return duration;
};

export const getStartOfFirstNonWeekendDate = (date = new Date()) => {
  let currentDate = date;
  while (isWeekend(currentDate)) {
    currentDate = addDays(currentDate, 1);
  }

  return startOfDay(currentDate);
};

export const isValidDate = (dateString: string) => !isNaN(new Date(dateString).getTime());
